import { useEffect, type ButtonHTMLAttributes, type ReactNode } from "react";
import { ApiError } from "../api/client";
import { absoluteTime, relativeTime } from "../lib/format";

const STATUS_STYLES: Record<string, string> = {
  running: "bg-blue-50 text-blue-700 ring-blue-200",
  success: "bg-green-50 text-green-700 ring-green-200",
  approved: "bg-green-50 text-green-700 ring-green-200",
  partial: "bg-amber-50 text-amber-800 ring-amber-200",
  pending: "bg-amber-50 text-amber-800 ring-amber-200",
  failed: "bg-red-50 text-red-700 ring-red-200",
  rejected: "bg-red-50 text-red-700 ring-red-200",
  created: "bg-green-50 text-green-700 ring-green-200",
  updated: "bg-blue-50 text-blue-700 ring-blue-200",
  deleted: "bg-red-50 text-red-700 ring-red-200",
  retired: "bg-gray-100 text-gray-600 ring-gray-300",
  // system status checks
  ok: "bg-green-50 text-green-700 ring-green-200",
  error: "bg-red-50 text-red-700 ring-red-200",
  degraded: "bg-amber-50 text-amber-800 ring-amber-200",
  disabled: "bg-gray-50 text-gray-500 ring-gray-200",
  not_configured: "bg-gray-50 text-gray-500 ring-gray-200",
  // sync preview
  new: "bg-green-50 text-green-700 ring-green-200",
  changed: "bg-blue-50 text-blue-700 ring-blue-200",
  unchanged: "bg-gray-50 text-gray-500 ring-gray-200",
  removed: "bg-red-50 text-red-700 ring-red-200",
  // task states
  queued: "bg-amber-50 text-amber-800 ring-amber-200",
  // media transcription
  processing: "bg-blue-50 text-blue-700 ring-blue-200",
  skipped: "bg-gray-100 text-gray-600 ring-gray-300",
  // simulated patient scenarios and attempts
  draft: "bg-amber-50 text-amber-800 ring-amber-200",
  published: "bg-green-50 text-green-700 ring-green-200",
  archived: "bg-gray-100 text-gray-600 ring-gray-300",
  in_progress: "bg-blue-50 text-blue-700 ring-blue-200",
  completed: "bg-green-50 text-green-700 ring-green-200",
  abandoned: "bg-gray-100 text-gray-600 ring-gray-300",
};

export function StatusBadge({ status, label }: { status: string; label?: string }) {
  const style = STATUS_STYLES[status] ?? "bg-gray-50 text-gray-700 ring-gray-200";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${style}`}>
      {status === "running" && <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" />}
      {label ?? status}
    </span>
  );
}

export function RelativeTime({ iso }: { iso: string | null | undefined }) {
  return (
    <time dateTime={iso ?? undefined} title={absoluteTime(iso)}>
      {relativeTime(iso)}
    </time>
  );
}

export function Card({ title, actions, children, className = "" }: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`rounded-lg border border-gray-200 bg-white dark:border-[#1E2E4D] dark:bg-panel ${className}`}>
      {(title || actions) && (
        <header className="flex items-center justify-between gap-2 border-b border-gray-200 px-4 py-3 dark:border-[#1E2E4D]">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-slate-100">{title}</h2>
          {actions && <div className="flex gap-2">{actions}</div>}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-gray-600 dark:text-slate-400">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

type Variant = "primary" | "secondary" | "danger" | "brand";
const VARIANTS: Record<Variant, string> = {
  primary: "bg-gray-900 text-white hover:bg-gray-700 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white",
  secondary: "bg-white text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:bg-transparent dark:text-slate-100 dark:ring-[#2A3A5A] dark:hover:bg-white/5",
  danger: "bg-red-600 text-white hover:bg-red-500",
  // Deep-teal blue used by the dashboard/shell design.
  brand: "bg-brand text-white hover:bg-brand-dark",
};

export function Button({ variant = "secondary", loading, children, className = "", ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  loading?: boolean;
}) {
  return (
    <button
      {...rest}
      disabled={rest.disabled || loading}
      className={`inline-flex items-center justify-center rounded px-3 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50 ${VARIANTS[variant]} ${className}`}
    >
      {loading ? "Working…" : children}
    </button>
  );
}

// 502/503 from pipeline routes mean an upstream (Moodle, Elasticsearch, the
// embedding service, Ollama) is down or misconfigured, not a UI bug.
export function ErrorBanner({ error }: { error: unknown }) {
  if (!error) return null;
  let message = error instanceof Error ? error.message : String(error);
  if (error instanceof ApiError && error.status === 0) {
    // Network/CORS failure: the client's message already says what to check.
  } else if (error instanceof ApiError && (error.status === 502 || error.status === 503)) {
    message = `Upstream service unavailable (${error.status}): ${message}`;
  } else if (error instanceof ApiError && error.status === 403) {
    message = "Your role can't perform this action.";
  }
  return (
    <div role="alert" className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      {message}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="py-6 text-center text-sm text-gray-500 dark:text-slate-400">{children}</p>;
}

export function Loading() {
  return <p className="py-6 text-center text-sm text-gray-500 dark:text-slate-400">Loading…</p>;
}

export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-slate-400">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-gray-900 dark:text-slate-100">{value}</div>
      {hint && <div className="mt-0.5 text-xs text-gray-500 dark:text-slate-400">{hint}</div>}
    </div>
  );
}

export function Th({ children, className = "", title }: { children?: ReactNode; className?: string; title?: string }) {
  return <th title={title} className={`px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400 ${className}`}>{children}</th>;
}

export function Td({ children, className = "", colSpan }: { children?: ReactNode; className?: string; colSpan?: number }) {
  return <td colSpan={colSpan} className={`px-3 py-2 text-sm ${className}`}>{children}</td>;
}

// "Showing 51–100 of 340" with Previous/Next, driven by the API's `total`.
export function Pager({ offset, limit, count, total, onChange }: {
  offset: number;
  limit: number;
  count: number; // rows on this page
  total: number | undefined;
  onChange: (offset: number) => void;
}) {
  const hasNext = total !== undefined ? offset + count < total : count === limit;
  return (
    <div className="mt-3 flex items-center justify-between text-sm text-gray-500">
      <span>
        {count ? `Showing ${offset + 1}–${offset + count}` : ""}
        {count && total !== undefined ? ` of ${total.toLocaleString()}` : ""}
      </span>
      <div className="flex gap-2">
        <Button disabled={offset === 0} onClick={() => onChange(Math.max(0, offset - limit))}>Previous</Button>
        <Button disabled={!hasNext} onClick={() => onChange(offset + limit)}>Next</Button>
      </div>
    </div>
  );
}

export function Modal({ title, onClose, children, footer, wide = false }: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/30 p-4 sm:p-10" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        className={`flex max-h-[calc(100vh-5rem)] w-full flex-col rounded-lg bg-white shadow-xl dark:bg-panel ${wide ? "max-w-4xl" : "max-w-lg"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-2 border-b px-4 py-3 dark:border-[#1E2E4D]">
          <h2 className="font-semibold dark:text-slate-100">{title}</h2>
          <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-900 dark:text-slate-400 dark:hover:text-white" aria-label="Close">✕</button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>
        {footer && <footer className="flex flex-wrap items-center justify-end gap-2 border-t px-4 py-3">{footer}</footer>}
      </div>
    </div>
  );
}
